import axios from 'axios';

/**
 * Cliente axios del módulo Incorporaciones / Bajas. Mismo patrón que soporte/api.js:
 * baseURL = REACT_APP_API_URL + prefijo, interceptor de REQUEST que añade el Bearer desde
 * localStorage. SIN manejo de 401 (lo gestiona AuthManager globalmente, confirmado en §3.1).
 * Solo exports nombrados (no default).
 *
 * DECISIÓN (dos routers backend: /api/incorporaciones y /api/bajas): se crean DOS instancias
 * axios (una por prefijo) mediante una factory compartida `crearApi`, en lugar de una sola sin
 * baseURL de módulo. Motivo: conservar el patrón de Soporte (baseURL por prefijo) sin duplicar
 * el interceptor. Soporte tiene un único prefijo, así que esto es una extensión mínima del patrón.
 */
function crearApi(prefijo) {
  const API = axios.create({ baseURL: `${process.env.REACT_APP_API_URL}${prefijo}` });
  API.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  });
  return API;
}

const API_INCORPORACIONES = crearApi('/api/incorporaciones');
const API_BAJAS = crearApi('/api/bajas');
// Instancia a la RAÍZ /api (con Bearer) para catálogos del proyecto que el wizard reutiliza
// (departamentos / oficinas): son endpoints del core, no del módulo.
const API_RAIZ = crearApi('');

// Cliente PÚBLICO del formulario del candidato (Fase 8): SIN interceptor de Bearer. Es una superficie
// sin sesión; la credencial es el token de la URL. No se añade Authorization (el candidato no tiene
// token en localStorage, y uno rancio de otra sesión no debe colarse aquí).
const API_PUBLICO = axios.create({ baseURL: `${process.env.REACT_APP_API_URL}/api/incorporaciones-publico` });

// — Incorporaciones (altas)
/**
 * ── LA ÚNICA TRADUCCIÓN ENTRE LA URL Y EL SERVIDOR ───────────────────────────────
 *
 * La URL guarda el ámbito como `internos`/`colaboradores` —son los valores del selector de
 * pestañas— y el servidor sólo entiende `interno`/`colaborador`. Es la única pareja de nombres
 * que no coincide, y por eso tiene que existir **en un solo sitio**.
 *
 * El 05/10 existía en dos: el listado traducía y el botón de exportar mandaba `location.search`
 * tal cual. Resultado: el fichero ignoraba la pestaña y bajaba las 11 filas mientras el botón
 * anunciaba 2. **No falló**, que es lo peor: el parámetro llegaba con el nombre bueno y un valor
 * que el servidor descartaba, y eso se comporta igual que un parámetro que no llegó.
 *
 * Y es una **lista blanca**: lo que no esté aquí no viaja. Así `pagina` y `alta` —que viven en la
 * misma URL— no se cuelan en la exportación por construcción, no por acordarse de quitarlos.
 */
const AMBITO_URL_A_SERVIDOR = { internos: 'interno', colaboradores: 'colaborador' };
/* `orden` y `dir` entran en la lista: el fichero tiene que salir **en el mismo orden que la
 * tabla**. Dejarlos fuera hacia que ordenar por Estado y exportar diera un Excel ordenado por
 * fecha —«lo que se descarga es lo que se ve» tambien es el orden, no solo las filas—. */
const FILTROS_TAL_CUAL = ['kpi', 'empresa', 'razon_social', 'desde', 'hasta', 'orden', 'dir'];

export const filtrosDelEmbudo = (busqueda) => {
  const p = new URLSearchParams(String(busqueda || '').replace(/^\?/, ''));
  // Sin parámetro, la pantalla enseña Internos: el filtro que se manda tiene que ser el mismo que
  // la pestaña que se está viendo, o el fichero vuelve a traer de más.
  const q = { ambito: AMBITO_URL_A_SERVIDOR[p.get('ambito')] || 'interno' };
  FILTROS_TAL_CUAL.forEach((k) => { if (p.get(k)) q[k] = p.get(k); });
  if (p.get('sin_fecha') === '1') q.sin_fecha = '1';
  return q;
};

export const listIncorporaciones = (params) => API_INCORPORACIONES.get('/', { params }).then((r) => r.data);

/**
 * Lo mismo para Bajas. Aqui el ambito tambien se traduce -la URL dice `internos`, el servidor
 * entiende `interno`-, asi que la misma trampa y la misma solucion: una lista blanca, una
 * traduccion, y la usan el listado y el Excel.
 */
export const filtrosDeBajas = (busqueda) => {
  const p = new URLSearchParams(String(busqueda || '').replace(/^\?/, ''));
  const q = { ambito: AMBITO_URL_A_SERVIDOR[p.get('ambito')] || 'interno' };
  ['estado', 'orden', 'dir'].forEach((k) => { if (p.get(k)) q[k] = p.get(k); });
  return q;
};
export const getIncorporacion = (id) => API_INCORPORACIONES.get(`/${id}`).then((r) => r.data);

// — Bajas (Fase 11)
export const listBajas = (params) => API_BAJAS.get('/', { params }).then((r) => r.data);
export const getBaja = (id) => API_BAJAS.get(`/${id}`).then((r) => r.data);

// Las DOS preguntas sobre una persona, ANTES de crear la baja. Son distintas a propósito:
//   · queTienePersona  → qué material y qué credenciales tiene, para enseñarlo
//   · bajaAbiertaDePersona → si ya hay un proceso abierto, que es el guard del diálogo
// Mezclarlas obligaría a la ficha a pedir el inventario entero sólo para saber si puede crear.
/* A quien MAS avisar de una baja. Se manda la lista COMPLETA, no la pieza: es un conjunto, y con
 * dos llamadas un fallo entre medias dejaria una lista que nadie pidio.
 *
 * OJO con lo que significa: esta lista SUMA a los destinatarios que ya salen de las tareas.
 * Quitar un area de aqui NO silencia a quien tenga una tarea suya en esta baja. */
/* `destinatarios` es la lista COMPLETA, con las dos clases mezcladas:
 *     [{ area_id: 3 }, { email: 'gestoria@x.com' }]
 * El servidor sigue aceptando `areas: [ids]` por compatibilidad, pero desde aqui se manda siempre
 * la forma nueva: dos formas de llamar es como una de las dos se queda sin actualizar. */
export const editarDestinatariosBaja = (bajaId, destinatarios) =>
  API_BAJAS.put(`/${bajaId}/areas`, { destinatarios }).then((r) => r.data);

/* Volver a avisar. Por defecto SOLO a quien no consta ya avisado; `todos` lo repite entero.
 * Devuelve `compuestos`, `ya_avisados` y `omitidos` para poder decirlo con numeros. */
export const renotificarBaja = (bajaId, todos = false) =>
  API_BAJAS.post(`/${bajaId}/notificar`, { todos }).then((r) => r.data);

export const queTienePersona = (ambito, id) => API_BAJAS.get(`/persona/${ambito}/${id}/tiene`).then((r) => r.data);
export const bajaAbiertaDePersona = (ambito, id) => API_BAJAS.get(`/persona/${ambito}/${id}/abierta`).then((r) => r.data);

// Buscar a quien dar de baja, en LOS DOS EJES y bajo `bajas.crear`. Endpoint propio: el de
// Incorporaciones es solo-internos por contrato y pide otros permisos.
export const buscarPersonasBaja = (q, ambito) =>
  API_BAJAS.get('/personas/buscar', { params: { q, ambito } }).then((r) => r.data);

export const crearBaja = (payload) => API_BAJAS.post('/', payload).then((r) => r.data);
/**
 * Editar la fecha o el motivo de una baja abierta.
 *
 * La fecha NO es un dato mas: si cruza la linea de hoy, la persona pierde o recupera el acceso a
 * la intranet. La respuesta trae `efecto` diciendo que le paso —`desactivada`, `reactivada`,
 * `ninguna`—, y la pantalla lo cuenta en vez de callarlo.
 */
export const editarBaja = (id, payload) => API_BAJAS.patch(`/${id}`, payload).then((r) => r.data);
export const cancelarBaja = (id) => API_BAJAS.post(`/${id}/cancelar`).then((r) => r.data);

// Acciones sobre una baja abierta (§4.4).
export const cambiarEstadoTareaBaja = (id, tareaId, estado) =>
  API_BAJAS.patch(`/${id}/tareas/${tareaId}`, { estado }).then((r) => r.data);
export const revocarCredencialBaja = (id, credId) =>
  API_BAJAS.post(`/${id}/credenciales/${credId}/revocar`).then((r) => r.data);
// La DEDUCIDA no tiene fila todavía: se identifica por su recurso, y revocarla la crea ya
// revocada. Endpoint aparte porque los dos actos no son el mismo.
export const revocarCredencialDeducidaBaja = (id, recursoId) =>
  API_BAJAS.post(`/${id}/credenciales/recurso/${recursoId}/revocar`).then((r) => r.data);
// Deshacer un clic equivocado en «Revocar». Sólo lo acepta si la credencial era DEDUCIDA (la
// revocación creó su fila) y la baja está abierta; el detalle lo dice en `deshacible`.
export const deshacerRevocacionBaja = (id, credId) =>
  API_BAJAS.post(`/${id}/credenciales/${credId}/deshacer-revocacion`).then((r) => r.data);
// Tareas a mano (§4.6). La descripcion es lo unico obligatorio; area y responsable, opcionales.
export const crearTareaBaja = (id, payload) => API_BAJAS.post(`/${id}/tareas`, payload).then((r) => r.data);
export const eliminarTareaBaja = (id, tareaId) => API_BAJAS.delete(`/${id}/tareas/${tareaId}`).then((r) => r.data);
// La tarea de una pieza que se le asignó después de crear la baja (18/10), ya enlazada a ella.
export const crearTareaMaterialBaja = (id, tipoId, assetId) =>
  API_BAJAS.post(`/${id}/tareas-material`, { inv_asset_type_id: tipoId, inv_asset_id: assetId }).then((r) => r.data);

export const cierrePrevioBaja = (id) => API_BAJAS.get(`/${id}/cierre-previo`).then((r) => r.data);
export const cerrarBaja = (id, confirmaciones) => API_BAJAS.post(`/${id}/cerrar`, confirmaciones || {}).then((r) => r.data);

// — Solicitudes de datos al candidato (Fase 8). crear/reenviar devuelven `enlace` UNA vez.
export const listSolicitudes = () => API_INCORPORACIONES.get('/solicitudes').then((r) => r.data);
export const crearSolicitud = (payload) => API_INCORPORACIONES.post('/solicitudes', payload).then((r) => r.data);
export const getSolicitud = (id) => API_INCORPORACIONES.get(`/solicitudes/${id}`).then((r) => r.data);
export const reenviarSolicitud = (id) => API_INCORPORACIONES.post(`/solicitudes/${id}/reenviar`).then((r) => r.data);
export const cancelarSolicitud = (id) => API_INCORPORACIONES.post(`/solicitudes/${id}/cancelar`).then((r) => r.data);
export const borrarDatosCandidato = (id) => API_INCORPORACIONES.post(`/solicitudes/${id}/borrar-datos`).then((r) => r.data);
// Foto del DNI de una solicitud (blob), para transcribir el DNI al completar el alta. Devuelve un Blob.
export const descargarDocumentoDni = (id, cara) => API_INCORPORACIONES.get(`/solicitudes/${id}/documento`, { params: { cara }, responseType: 'blob' }).then((r) => r.data);

// — Formulario público del candidato (Fase 8). Sin sesión; token en la URL.
export const cargarFormularioPublico = (token) => API_PUBLICO.get(`/formulario/${token}`).then((r) => r.data);
export const guardarFormularioPublico = (token, payload) => API_PUBLICO.post(`/formulario/${token}`, payload).then((r) => r.data);
export const subirDocumentoPublico = (token, file, cara) => {
  const fd = new FormData();
  fd.append('imagen', file);
  if (cara) fd.append('cara', cara);
  return API_PUBLICO.post(`/formulario/${token}/documento`, fd).then((r) => r.data);
};

// — Configuración · Áreas (Fase 4)
/* ── MOTIVOS DE BAJA ───────────────────────────────────────────────────────────────────────────
 * `listMotivosBaja` devuelve `{ motivos: [{ id, nombre, orden, activo, usos }] }`. El `usos` viene
 * CON la lista y no se pide al borrar: asi la pantalla puede avisar de cuantas bajas usan un motivo
 * ANTES de que alguien pulse, en vez de enterarse por un 409. */
/* ── TAREAS FIJAS DE BAJA ──────────────────────────────────────────────────────────────────────
 * Ojo con el nombre: ya existian `crearTareaBaja`/`eliminarTareaBaja`, que crean una tarea EN UNA
 * BAJA concreta. Estas son del CATALOGO —las que se crearan en todas— y por eso llevan el sufijo.
 * Dos cosas parecidas con el mismo nombre es como se acaba llamando a la que no era. */
export const listTareasBaja = () => API_INCORPORACIONES.get('/tareas-baja').then((r) => r.data);
export const crearTareaBajaCatalogo = (payload) => API_INCORPORACIONES.post('/tareas-baja', payload).then((r) => r.data);
export const actualizarTareaBajaCatalogo = (id, payload) => API_INCORPORACIONES.put(`/tareas-baja/${id}`, payload).then((r) => r.data);
export const desactivarTareaBajaCatalogo = (id) => API_INCORPORACIONES.delete(`/tareas-baja/${id}`).then((r) => r.data);
export const eliminarTareaBajaCatalogoPermanente = (id) => API_INCORPORACIONES.delete(`/tareas-baja/${id}/permanente`).then((r) => r.data);

export const listMotivosBaja = () => API_INCORPORACIONES.get('/motivos-baja').then((r) => r.data);
export const crearMotivoBaja = (payload) => API_INCORPORACIONES.post('/motivos-baja', payload).then((r) => r.data);
export const actualizarMotivoBaja = (id, payload) => API_INCORPORACIONES.put(`/motivos-baja/${id}`, payload).then((r) => r.data);
export const desactivarMotivoBaja = (id) => API_INCORPORACIONES.delete(`/motivos-baja/${id}`).then((r) => r.data);
export const eliminarMotivoBajaPermanente = (id) => API_INCORPORACIONES.delete(`/motivos-baja/${id}/permanente`).then((r) => r.data);

export const listAreas = () => API_INCORPORACIONES.get('/areas').then((r) => r.data);
export const crearArea = (payload) => API_INCORPORACIONES.post('/areas', payload).then((r) => r.data);
export const actualizarArea = (id, payload) => API_INCORPORACIONES.put(`/areas/${id}`, payload).then((r) => r.data);
export const desactivarArea = (id) => API_INCORPORACIONES.delete(`/areas/${id}`).then((r) => r.data);
export const anadirMiembroArea = (areaId, personaId) =>
  API_INCORPORACIONES.post(`/areas/${areaId}/miembros`, { persona_id: personaId }).then((r) => r.data);
export const quitarMiembroArea = (areaId, personaId) =>
  API_INCORPORACIONES.delete(`/areas/${areaId}/miembros/${personaId}`).then((r) => r.data);

// — Personas internas para los selectores de persona (solo internos y activos). Desde Fase 10 el
// permiso es CUALQUIERA de ver/crear/completar/configurar, no solo `.configurar`: además de los
// miembros de área y las personas nombradas en un aviso, ahora lo usa el selector de responsable del
// alta y de cada tarea, y quien crea un alta puede no tener `.configurar`.
export const buscarPersonasIncorp = (q) =>
  API_INCORPORACIONES.get('/personas/buscar', { params: { q } }).then((r) => r.data);

// — Configuración · Recursos (Fase 4)
export const listRecursos = () => API_INCORPORACIONES.get('/recursos').then((r) => r.data);
// Uno solo, para la página de edición: evita cargar el catálogo entero solo para leer una fila.
export const getRecurso = (id) => API_INCORPORACIONES.get(`/recursos/${id}`).then((r) => r.data);
export const crearRecurso = (payload) => API_INCORPORACIONES.post('/recursos', payload).then((r) => r.data);
export const actualizarRecurso = (id, payload) => API_INCORPORACIONES.put(`/recursos/${id}`, payload).then((r) => r.data);
export const desactivarRecurso = (id) => API_INCORPORACIONES.delete(`/recursos/${id}`).then((r) => r.data);

// — Tipos de activo del catálogo de Inventario (para el desplegable de Recursos).
export const listTiposActivo = () => API_INCORPORACIONES.get('/tipos-activo').then((r) => r.data);

// Las cuentas compartidas (18/10) ya no están aquí: pasaron a Personas › Buzones y listas (30/10),
// con su cliente en `componentes/correos/api.js` y su API en el núcleo (`/api/cuentas-compartidas`).

// — Configuración · Puestos (Fase 5)
export const listPuestos = () => API_INCORPORACIONES.get('/puestos').then((r) => r.data);

/* ── QUIEN PUEDE DAR DE ALTA EN QUE PUESTO (03/10) ────────────────────────────────────────
 *
 * `alcanceMio` lo usa el WIZARD para acotar su desplegable y para EXPLICAR por que esta
 * acotado. No se filtra `/puestos` en su lugar: esa lista la usa tambien Configuracion >
 * Puestos, que los necesita TODOS para administrarlos. Una pregunta distinta, su endpoint. */
export const alcanceMio = () => API_INCORPORACIONES.get('/alcance/mio').then((r) => r.data);
export const alcanceDePersona = (personaId) =>
  API_INCORPORACIONES.get(`/alcance/persona/${personaId}`).then((r) => r.data);
export const listPersonasDelPuesto = (puestoId) =>
  API_INCORPORACIONES.get(`/puestos/${puestoId}/personas`).then((r) => r.data);
export const vincularPersonaAPuesto = (puestoId, personaId) =>
  API_INCORPORACIONES.post(`/puestos/${puestoId}/personas`, { persona_id: personaId }).then((r) => r.data);
export const desvincularPersonaDePuesto = (puestoId, personaId) =>
  API_INCORPORACIONES.delete(`/puestos/${puestoId}/personas/${personaId}`).then((r) => r.data);
export const crearPuesto = (payload) => API_INCORPORACIONES.post('/puestos', payload).then((r) => r.data);
export const actualizarPuesto = (id, payload) => API_INCORPORACIONES.put(`/puestos/${id}`, payload).then((r) => r.data);
export const desactivarPuesto = (id) => API_INCORPORACIONES.delete(`/puestos/${id}`).then((r) => r.data);

// — Negocios (module-local read-only) para los desplegables de Puestos/Perfiles.
export const listNegocios = () => API_INCORPORACIONES.get('/negocios').then((r) => r.data);

// — Configuración · Perfiles (Fase 5)
export const listPerfiles = () => API_INCORPORACIONES.get('/perfiles').then((r) => r.data);
export const getPerfil = (id) => API_INCORPORACIONES.get(`/perfiles/${id}`).then((r) => r.data);
export const crearPerfil = (payload) => API_INCORPORACIONES.post('/perfiles', payload).then((r) => r.data);
export const actualizarPerfil = (id, payload) => API_INCORPORACIONES.put(`/perfiles/${id}`, payload).then((r) => r.data);
export const desactivarPerfil = (id) => API_INCORPORACIONES.delete(`/perfiles/${id}`).then((r) => r.data);

// — Roles del sistema (module-local read-only) para el desplegable de rol_sistema.
export const listRoles = () => API_INCORPORACIONES.get('/roles').then((r) => r.data);

// — Razones sociales para el desplegable de empresa contratante (module-local read-only).
// NO se usa `GET /api/empresas`: aquel esta acotado por ROL y el wizard se gobierna por PERMISO,
// asi que un usuario legitimo con `incorporaciones.crear` se llevaria un 403 y un selector vacio.
/**
 * Las altas de UNA persona, para el banner de la ficha y su tabla de procesos.
 *
 * Vive en el router que se monta SIEMPRE, asi que funciona con el modulo apagado — que es como
 * esta produccion. Si colgara del router del modulo, la ficha no veria nada alli.
 *
 * Devuelve `{ tiene_alta_abierta, abierta, todas }`. Una peticion para las dos cosas: el banner
 * y la tabla miran lo mismo desde sitios distintos.
 */
export const altasDePersona = (ambito, id) =>
  API_INCORPORACIONES.get(`/altas/persona/${ambito}/${id}`).then((r) => r.data);

export const listEmpresasContratantes = () =>
  API_INCORPORACIONES.get('/empresas-contratantes').then((r) => r.data);

// — Alta transaccional (Fase 9). `.crear` (manual) / `.completar` (desde solicitud, si va solicitud_id).
export const crearAltaIncorp = (payload) => API_INCORPORACIONES.post('/altas', payload).then((r) => r.data);

// — Comprobación PREVIA de DNI (adelanta lo que crearAlta bloquea/avisa). `.crear`.
export const comprobarDniIncorp = (dni) => API_INCORPORACIONES.get('/comprobar-dni', { params: { dni } }).then((r) => r.data);

// — Panel de detalle del alta (Fase 10). El detalle trae las OCHO secciones en UNA petición; no hay
// que encadenar llamadas ni acoplarlas con Promise.all (la deuda de las cuatro pantallas antiguas).
// OJO: `getIncorporacion` de arriba apunta a `/:id`, que es el stub que sigue devolviendo 404. El
// detalle del alta vive en `/altas/:id`.
export const getAlta = (id) => API_INCORPORACIONES.get(`/altas/${id}`).then((r) => r.data);

/* Editar el alta abierta. El servidor sólo acepta `fecha_incorporacion` y `responsable_id`: es una
 * lista blanca allí, no una convención de aquí. Y devuelve `efecto`, porque cambiar la fecha puede
 * dar o quitar el acceso a una persona y eso hay que decirlo. */
/* Material del alta: añadir y quitar. El catálogo para el desplegable NO necesita endpoint nuevo —
 * `listRecursos` ya existe y lo usa la pestaña de credenciales. */
export const anadirRecursoAlta = (altaId, recursoId) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/recursos`, { recurso_id: recursoId }).then((r) => r.data);

/* Devuelve `progreso_antes` y `progreso`: quitar un pendiente SUBE el porcentaje, y el aviso tiene
 * que poder decir de cuánto a cuánto. */
export const quitarRecursoAlta = (altaId, filaId) =>
  API_INCORPORACIONES.delete(`/altas/${altaId}/recursos/${filaId}`).then((r) => r.data);

/* La fila VIRTUAL de la tabla de procesos: `personas.fecha_alta` / `fecha_baja` cuando ningún
 * proceso abierto las gobierna. Lista blanca de dos campos, en el servidor. */
export const fechaManualDePersona = (ambito, id, campo, fecha) =>
  API_INCORPORACIONES.patch(`/altas/persona/${ambito}/${id}/fecha-manual`, { campo, fecha })
    .then((r) => r.data);

/* Las areas avisadas: se manda la LISTA COMPLETA, no la pieza. Es un conjunto, y el servidor hace
 * el diferencial — con dos llamadas, un fallo entre medias deja el alta sin ninguna. */
export const editarDestinatariosAlta = (altaId, destinatarios) =>
  API_INCORPORACIONES.put(`/altas/${altaId}/areas`, { destinatarios }).then((r) => r.data);

/* Volver a avisar. Por defecto SOLO a quien no consta ya avisado; `todos` lo repite a la lista
 * entera. Devuelve `compuestos` y `ya_avisados` para poder decirlo con numeros. */
/* Comunicar el correo y el telefono asignados. NO es un reenvio: los destinatarios van EN LA
 * PETICION, elegidos en el modal cada vez. Por eso no hay `todos` ni «solo a los nuevos». */
export const avisarDatosAlta = (altaId, destinatarios) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/avisar-datos`, { destinatarios }).then((r) => r.data);

export const renotificarAlta = (altaId, todos = false) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/notificar`, { todos }).then((r) => r.data);

export const editarAlta = (id, payload) =>
  API_INCORPORACIONES.patch(`/altas/${id}`, payload).then((r) => r.data);

// Seguimiento: estado de tarea y de material. Devuelven el progreso recalculado por el backend, que
// es la única fuente del porcentaje (no se almacena en ninguna columna).
export const estadoTareaAlta = (altaId, tareaId, estado) =>
  API_INCORPORACIONES.patch(`/altas/${altaId}/tareas/${tareaId}`, { estado }).then((r) => r.data);
export const crearTareaAlta = (altaId, payload) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/tareas`, payload).then((r) => r.data);
export const eliminarTareaAlta = (altaId, tareaId) =>
  API_INCORPORACIONES.delete(`/altas/${altaId}/tareas/${tareaId}`).then((r) => r.data);
/* `invAssetId`: sin pasar = no se toca el activo; `null` = soltarlo; número = ese activo.
 * Desde el 15/10 esto toca Inventario: «hecho» con activo lo ASIGNA y desmarcar lo DEVUELVE. */
/* `confirmarUnaPorPersona` (01/11): la persona ya tiene otro del tipo y el usuario confirmó el aviso. */
export const estadoRecursoAlta = (altaId, filaId, estado, invAssetId, confirmarUnaPorPersona = false) =>
  API_INCORPORACIONES.patch(`/altas/${altaId}/recursos/${filaId}`, {
    estado,
    ...(invAssetId === undefined ? {} : { inv_asset_id: invAssetId }),
    ...(confirmarUnaPorPersona ? { confirmar_una_por_persona: true } : {}),
  }).then((r) => r.data);
// Los activos que se pueden reservar o entregar en una línea de material del alta.
export const activosDeRecursoAlta = (altaId, filaId) =>
  API_INCORPORACIONES.get(`/altas/${altaId}/recursos/${filaId}/activos`).then((r) => r.data);

// ── Credenciales · el ciclo completo. Permiso `incorporaciones.credenciales`. ──
// Sustituyen a `asignarCredencialesAlta`, que escribía las columnas de la persona sin pasar por
// ningún recurso y se retiró con el backend del ciclo.
//
// El ENVÍO del acceso no tiene función aquí todavía: depende del puente de Vaultwarden.

export const crearCredencialAlta = (altaId, recursoId) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/credenciales`, { recurso_id: recursoId }).then((r) => r.data);

// `valor` solo para las credenciales con campo de identidad que NO sea el correo: el correo es de
// solo lectura y el backend rechaza el intento de cambiarlo.
export const marcarCredencialAlta = (altaId, credId, payload) =>
  API_INCORPORACIONES.patch(`/altas/${altaId}/credenciales/${credId}`, payload).then((r) => r.data);

export const revocarCredencialAlta = (altaId, credId) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/credenciales/${credId}/revocar`).then((r) => r.data);

// Cierre manual del alta. Permiso `.editar`.
export const cerrarAlta = (altaId) => API_INCORPORACIONES.post(`/altas/${altaId}/cerrar`).then((r) => r.data);

// Pieza 5: cancelar un alta, deshacer lo que hizo y volver si fue un error. Permiso
// `incorporaciones.cancelar_alta`, salvo ver las tareas de deshacer (`.ver`).
export const cancelarAlta = (altaId) => API_INCORPORACIONES.post(`/altas/${altaId}/cancelar`).then((r) => r.data);
export const getDeshacerAlta = (altaId) => API_INCORPORACIONES.get(`/altas/${altaId}/deshacer`).then((r) => r.data);
// `estado`: 'completada' | 'pendiente'. Marcar LO HACE: devuelve en Inventario o revoca la credencial.
export const marcarDeshacerAlta = (altaId, tareaId, estado) =>
  API_INCORPORACIONES.patch(`/altas/${altaId}/deshacer/${tareaId}`, { estado }).then((r) => r.data);
export const volverAltaEnCurso = (altaId) => API_INCORPORACIONES.post(`/altas/${altaId}/volver`).then((r) => r.data);
// Pieza 5.3: si se puede borrar y qué se borraría (`.ver`), y borrar (`.borrar_alta`). Irreversible:
// `numero` tecleado y una confirmación por cada tarea que la intranet no puede comprobar.
export const getBorradoAlta = (altaId) => API_INCORPORACIONES.get(`/altas/${altaId}/borrado`).then((r) => r.data);
export const borrarAltaEntera = (altaId, numero, confirmaciones) =>
  API_INCORPORACIONES.post(`/altas/${altaId}/borrar`, { numero, confirmaciones }).then((r) => r.data);

// — Catálogos del core reutilizados por el wizard (departamentos / oficinas).
export const listDepartamentos = () => API_RAIZ.get('/api/departamentos').then((r) => r.data);
export const listOficinas = () => API_RAIZ.get('/api/oficinas').then((r) => r.data);
// Negocios del CORE con sus Departamentos anidados (vía DepartamentoEmpresa) → para filtrar el
// desplegable de departamento por el negocio elegido.
export const listNegociosCore = () => API_RAIZ.get('/api/negocios').then((r) => r.data);
// Empresas colaboradoras del CORE — solo para el camino COLABORADOR del wizard: la persona
// colaboradora pertenece a una de ellas (`personas_colaboradoras.empresa_colaboradora_id`) y sin
// ese dato queda mal creada. Endpoint ya existente (solo authMiddleware, sin checkRol), igual que
// /api/oficinas y /api/negocios: no hizo falta crear ninguno. Devuelve el array plano por nombre.
/*
 * NORMALIZA `Id` A `id`, Y NO ES COSMETICA: ES UN FALLO QUE YA PASO.
 *
 * Este endpoint devuelve `Id` con MAYUSCULA; su vecino `/api/incorporaciones/negocios` devuelve
 * `id`. Dos convenciones conviviendo, y nada obliga a que coincidan.
 *
 * Lo que lo hace invisible: con `<option key={c.id} value={c.id}>` y `c.id` a `undefined`, React
 * **omite el atributo `value`**, y un `<option>` sin `value` usa su TEXTO como valor. El selector
 * se ve relleno y correcto —«TERRASSA SERVEIS DE PREVENCIO, S.L.»— y lo que viaja al servidor es
 * ese nombre en el campo del id. No hay error, no hay aviso: llega otra cosa.
 *
 * Se normaliza AQUI, una vez, y no en cada pantalla: son tres llamantes y la siguiente que las use
 * no tiene que acordarse. `WizardAlta` ya lo hacia por su cuenta (`e.Id ?? e.id`) y por eso era el
 * unico que funcionaba —la solucion existia en el repo y no se uso—. Se conserva `Id` en el objeto
 * para no romper a quien ya lo leyera.
 */
export const listEmpresasColaboradoras = () => API_RAIZ.get('/api/empresas-colaboradoras')
  .then((r) => (Array.isArray(r.data) ? r.data : (r.data && r.data.empresas) || [])
    .map((e) => ({ ...e, id: e.Id ?? e.id })));

// — Configuración · Reglas de aviso (Fase 6)
export const listReglasAviso = () => API_INCORPORACIONES.get('/reglas-aviso').then((r) => r.data);
export const getReglaAviso = (id) => API_INCORPORACIONES.get(`/reglas-aviso/${id}`).then((r) => r.data);
export const crearReglaAviso = (payload) => API_INCORPORACIONES.post('/reglas-aviso', payload).then((r) => r.data);
export const actualizarReglaAviso = (id, payload) => API_INCORPORACIONES.put(`/reglas-aviso/${id}`, payload).then((r) => r.data);
export const desactivarReglaAviso = (id) => API_INCORPORACIONES.delete(`/reglas-aviso/${id}`).then((r) => r.data);
export const eliminarReglaAvisoPermanente = (id) => API_INCORPORACIONES.delete(`/reglas-aviso/${id}/permanente`).then((r) => r.data);
export const resolverDestinatarios = (negocioId, puestoId) =>
  API_INCORPORACIONES.get('/reglas-aviso/resolver', { params: puestoId ? { negocio_id: negocioId, puesto_id: puestoId } : { negocio_id: negocioId } }).then((r) => r.data);
export const diagnosticoReglas = () => API_INCORPORACIONES.get('/reglas-aviso/diagnostico').then((r) => r.data);

// — Borrado FÍSICO condicionado (§3.5b). Distinto de la baja lógica (delete a secas). Ruta explícita.
export const eliminarAreaPermanente = (id) => API_INCORPORACIONES.delete(`/areas/${id}/permanente`).then((r) => r.data);
export const eliminarRecursoPermanente = (id) => API_INCORPORACIONES.delete(`/recursos/${id}/permanente`).then((r) => r.data);
export const eliminarPuestoPermanente = (id) => API_INCORPORACIONES.delete(`/puestos/${id}/permanente`).then((r) => r.data);
export const eliminarPerfilPermanente = (id) => API_INCORPORACIONES.delete(`/perfiles/${id}/permanente`).then((r) => r.data);
